<template>
    <div class="page-container">
        <div class="area-container">
            <el-scrollbar>
                <el-tree
                    ref="area_tree"
                    node-key="id"
                    :data="treeData"
                    :props="{
                        label: 'name',
                        children: 'children'
                    }"
                    :expand-on-click-node="false"
                    :default-expanded-keys="default_expanded_keys"
                    highlight-current
                    @node-click="handleNodeClick"
                />
            </el-scrollbar>
        </div>
        <div class="data-container">
            <div class="tool-box">
                <el-button type="primary" :icon="Refresh" @click="getTableData">刷新</el-button>
                <el-button type="primary" :icon="Plus" plain @click="handleAdd" v-permission="['heating:data-push:add']">
                    添加
                </el-button>
            </div>
            <el-table class="data-table" :data="table_data" v-loading="tableLoading">
                <el-table-column label="序号" type="index" fixed="left" width="50px"></el-table-column>
                <el-table-column label="设备类型" min-width="100" prop="type">
                    <template #default="{ row }">
                        {{ row.type === 1 ? '温采传感器' : row.type === 2 ? '管道传感器' : '阀门控制器' }}
                    </template>
                </el-table-column>
                <el-table-column label="设备总数/在线/离线" min-width="150" prop="device_count">
                    <template #default="{ row }">
                        <template v-if="row.online_count >= 0">
                            {{ row.online_count + row.offline_count }} / {{ row.online_count }} / {{ row.offline_count }}
                        </template>
                    </template>
                </el-table-column>
                <el-table-column label="推送服务类型" min-width="100" prop="type">
                    <template #default="{ row }">
                        {{ row.type === 1 ? '温采数据推送' : row.type === 2 ? '管道数据推送' : '阀门数据推送' }}
                    </template>
                </el-table-column>
                <el-table-column label="通信协议" min-width="100" prop="communication_protocol">
                    <template #default="{ row }">
                        {{ row.communication_protocol === 1 ? 'HTTP' : row.communication_protocol === 2 ? 'MQTT' : '' }}
                    </template>
                </el-table-column>
                <el-table-column label="推送地址" min-width="100" prop="url"></el-table-column>
                <el-table-column label="推送IP" min-width="100" prop="ip"></el-table-column>
                <el-table-column label="推送端口" min-width="100" prop="port"></el-table-column>
                <el-table-column label="推送频率" min-width="100" prop="rate">
                    <template #default="{ row }">
                        <!--                    rate为分钟，转化为时分-->
                        <template v-if="row.rate">{{ Math.floor(row.rate / 60) }}小时{{ row.rate % 60 }}分钟</template>
                    </template>
                </el-table-column>
                <el-table-column label="最新推送时间" min-width="100" prop="last_push_time"></el-table-column>
                <el-table-column label="描述" min-width="100" prop="desc"></el-table-column>
                <el-table-column label="操作" fixed="right" width="230">
                    <template #default="{ row }">
                        <el-button
                            type="primary"
                            :icon="View"
                            link
                            size="small"
                            @click="viewDetail(row)"
                            v-permission="['heating:data-push:detail']"
                        >
                            详情
                        </el-button>
                        <el-button
                            type="primary"
                            :icon="Upload"
                            link
                            size="small"
                            @click="handleEdit(row)"
                            v-permission="['heating:data-push:edit']"
                        >
                            编辑服务
                        </el-button>
                        <el-button
                            type="primary"
                            :icon="Document"
                            link
                            size="small"
                            @click="viewPushLog(row)"
                            v-permission="['heating:data-push:push-log']"
                        >
                            推送日志
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
            <forms-dialog
                v-model="formDialogVisible"
                :isForm="isForm"
                :modelId="selectModelId"
                :detailData="detail_data"
                @updateForm="getTableData"
            />
        </div>
        <push-log class="push-log-container" v-if="pushLogVisible" v-model="pushLogVisible" :modelId="selectModelId" />
    </div>
</template>

<script setup>
import { nextTick, onMounted, ref } from 'vue'
import { Document, Plus, Refresh, Upload, View } from '@element-plus/icons'
import { getOptionTree } from '@/api/room.js'
import FormsDialog from './components/forms.vue'
import PushLog from '@/views/factory/data-push/PushLog.vue'
import { getPushSetList } from '@/api/heating/monitoring.js'

// 区域树形数据
const area_tree = ref(null)
const treeData = ref([])
const default_expanded_keys = ref([])

function getTreeData() {
    getOptionTree().then((data) => {
        default_expanded_keys.value = [data[0].id]
        data.forEach((a) => {
            if (a.children && a.children.length > 0) {
                a.children.forEach((b) => {
                    delete b.children
                })
            }
        })
        treeData.value = data
        nextTick(() => {
            current_node.value = treeData.value[0]
            getTableData()
        })
    })
}

//点击树形节点
const current_node = ref(null)
const handleNodeClick = (node) => {
    current_node.value = node
    getTableData()
}

const table_data = ref([])
const tableLoading = ref(true)
const total = ref(0)

// 获取列表数据
async function getTableData() {
    tableLoading.value = true
    if (current_node.value?.level === 0) {
        table_data.value = []
    } else {
        getPushSetList({
            building: current_node.value.id
        }).then((data) => {
            table_data.value = data
        })
    }
    tableLoading.value = false
}

// 表单弹窗
const formDialogVisible = ref(false)
const isForm = ref(false) // 是否是表单
const selectModelId = ref('') // 点中的模块id
const detail_data = ref({})

// 添加
function handleAdd() {
    isForm.value = true
    selectModelId.value = ''
    detail_data.value = {}
    formDialogVisible.value = true
}

// 查看详情
function viewDetail(row) {
    isForm.value = false
    selectModelId.value = row.id
    detail_data.value = row
    formDialogVisible.value = true
}

// 编辑服务
function handleEdit(row) {
    isForm.value = true
    selectModelId.value = row.id
    detail_data.value = { ...row, building: current_node.value.id }
    formDialogVisible.value = true
}

// 推送日志页面
const pushLogVisible = ref(false)

// 推送日志
function viewPushLog(row) {
    selectModelId.value = row.id
    pushLogVisible.value = true
}

onMounted(() => {
    getTreeData()
})
</script>

<style lang="scss" scoped>
.push-log-container {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 10;
    width: 100%;
    height: 100%;
}
</style>
